import {
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
} from '@angular/core';

/**
 * Apparition d'un bloc du rapport à l'entrée dans le viewport.
 *
 * Reprend le comportement `whileInView` du document d'origine, reconstruit sur
 * `IntersectionObserver`. La feuille de styles fait tout le travail visuel :
 * la directive se contente de poser `data-reveal`, de piloter `--reveal-delay`
 * et d'ajouter `is-revealed` une fois — puis de cesser d'observer, une
 * apparition ne se rejoue pas.
 *
 * Deux cas la court-circuitent entièrement :
 *  - `prefers-reduced-motion` — on révèle tout de suite, sans observateur ;
 *  - l'impression — une section jamais atteinte au défilement est mise en page
 *    mais invisible, et le PDF sortait alors des blocs blancs pleine hauteur.
 *    D'où `revealAll()`, appelé par le chemin d'impression avant le snapshot.
 */

/** Registre des instances vivantes, pour la révélation forcée avant impression. */
const REGISTRY = new Set<ReportRevealDirective>();

/**
 * Révèle immédiatement tous les blocs montés, délai remis à zéro.
 *
 * À appeler avant `window.print()` : le navigateur photographie le document
 * tel quel, sans attendre ni transition ni observateur.
 */
export function revealAll(): void {
  REGISTRY.forEach((directive) => directive.revealNow());
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Délai au-delà duquel un bloc s'affiche même sans l'avis de l'observateur.
 *
 * Assez long pour que l'apparition au défilement reste la règle sur une lecture
 * normale, assez court pour qu'une section jamais annoncée ne reste pas blanche
 * sous les yeux de l'utilisateur.
 */
const SAFETY_DELAY_MS = 4000;

@Directive({
  selector: '[s2eReveal]',
  standalone: true,
  host: { 'data-reveal': '' },
})
export class ReportRevealDirective implements OnInit, OnDestroy {
  private readonly host = inject(ElementRef).nativeElement as HTMLElement;

  /** Décalage d'apparition, en millisecondes — cascade d'une liste. */
  readonly revealDelay = input(0);

  private observer: IntersectionObserver | null = null;
  private safety: ReturnType<typeof setTimeout> | null = null;
  private revealed = false;

  constructor() {
    REGISTRY.add(this);
    effect(() => {
      /* Une fois révélé, le délai ne sert plus qu'à rejouer une transition qui
         n'aura pas lieu : on le laisse tel quel pour ne pas réanimer un bloc
         déjà en place lors d'un changement de langue. */
      if (this.revealed) return;
      this.host.style.setProperty('--reveal-delay', `${this.revealDelay()}ms`);
    });
  }

  ngOnInit(): void {
    if (window.matchMedia(REDUCED_MOTION_QUERY).matches || !('IntersectionObserver' in window)) {
      this.revealNow();
      return;
    }

    /* Seuil zéro, et surtout pas une fraction de la surface.
       Un seuil de 0,15 exige que 15 % de l'élément soit visible : pour un bloc
       plus haut que ~6,7 fois la fenêtre, ce ratio est *mathématiquement
       inatteignable* et la section reste invisible pour toujours. Le cas s'est
       produit — une matière à 103 compétences produit une table de plusieurs
       milliers de pixels. La marge basse de −5 % suffit à exprimer « franchement
       entré dans la fenêtre » sans dépendre de la hauteur du bloc. */
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.reveal();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -5% 0px' },
    );
    this.observer.observe(this.host);

    /* Filet de sécurité. Le rapport est un document : qu'il soit lu, exporté ou
       simplement laissé ouvert, aucune de ses sections ne doit pouvoir rester
       blanche. Si l'observateur n'a rien dit passé ce délai — conteneur de
       défilement inattendu, bloc rendu hors flux, onglet en arrière-plan qui ne
       compose pas — on montre le contenu. Une animation manquée ne se remarque
       pas ; une section manquante, si. */
    this.safety = setTimeout(() => this.revealNow(), SAFETY_DELAY_MS);
  }

  /** Apparition normale, délai respecté. */
  private reveal(): void {
    this.revealed = true;
    this.host.classList.add('is-revealed');
    this.disconnect();
  }

  /** Apparition forcée : plus de délai, le rendu doit être immédiat. */
  revealNow(): void {
    this.host.style.setProperty('--reveal-delay', '0ms');
    this.reveal();
  }

  private disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;

    if (this.safety !== null) {
      clearTimeout(this.safety);
      this.safety = null;
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    REGISTRY.delete(this);
  }
}
